import { ActionReference, GameObjectReference, GameState, Inventory, ReadingResult } from "@shared/types";
import { css, html } from "../helpers/webComponents";
import { GameRouteService } from "../services/GameRouteService";
import { CanvasComponent } from "./CanvasComponent";
import { GameEventService } from "../services/GameEventService";

/** CSS affecting the {@link } */
const styles: string = css`
    .close-btn {
        font-family: "Onesize";
        position: absolute;
        top: 15px;
        right: 15px;
        width: 48px;
        height: 48px;
        font-size: 24px;
        background-color: #a67c52;
        border: 2px solid #5a3f25;
        border-radius: 50%;
        color: white;
        cursor: var(--cursor-hover);
        z-index: 10;
        transition: all 0.2s ease;
        box-shadow: 0 2px 5px rgba(0,0,0,0.2);
    }

    .close-btn:hover {
        background-color: #c78d57;
        transform: scale(1.1);
        box-shadow: 0 3px 8px rgba(0,0,0,0.3);
    }

    .inventory-book {
        position: relative;
        width: 850px;
        height: 650px;
        margin: 0 auto;
        perspective: 1500px;
        background: transparent;
        border-radius: 8px;
        overflow: hidden;
    }

    .book-cover {
        position: absolute;
        width: 100%;
        height: 100%;
        background: linear-gradient(145deg, #6b3e1a, #9d5c2b);
        border-radius: 0 15px 15px 0;
        box-shadow: 15px 15px 30px rgba(0, 0, 0, 0.4),
                    inset 5px 5px 10px rgba(255, 255, 255, 0.1);
        z-index: 1;
        transform-style: preserve-3d;
        transform: rotateY(-5deg);
        transition: transform 0.5s ease;
    }

    .book-pages {
        position: absolute;
        width: 100%;
        height: 100%;
        display: flex;
        transform-style: preserve-3d;
        z-index: 2;
    }

    .page {
        width: 50%;
        height: calc(100% - 40px);
        padding: 20px;
        box-sizing: border-box;
        background: #f8f4e8;
        position: relative;
        overflow: hidden;
        margin-top: 20px;
        box-shadow: 0 0 20px rgba(0,0,0,0.1);
        overflow-y: auto; /* Enable vertical scrolling */
        overflow-x: hidden; /* Prevent horizontal scroll */
    }

    .left-page {
        border-radius: 5px 0 0 5px;
        background: linear-gradient(to right, #f8f4e8 95%, #e8e0d0);
    }

    .right-page {
        border-radius: 0 5px 5px 0;
        background: linear-gradient(to left, #f8f4e8 95%, #e8e0d0);
    }

    .page-content {
        height: auto;
        min-height: 100%;
        display: flex;
        flex-direction: column;
        position: relative;
        z-index: 2;
    }

    .page-title {
        color: #5a3921;
        text-shadow: 1px 1px 2px rgba(0,0,0,0.1);
        letter-spacing: 1px;
        margin-bottom: 25px;
        padding-bottom: 10px;
        border-bottom: 3px double #8B4513;
    }

    .items-list {
        flex: 1;
        overflow-y: auto;
        padding-right: 10px;
        scrollbar-width: thin;
        scrollbar-color: #8B4513 #f8f4e8;
    }

    .items-list::-webkit-scrollbar {
        width: 6px;
    }

    .items-list::-webkit-scrollbar-thumb {
        background-color: #8B4513;
        border-radius: 3px;
    }

    .item {
        display: flex;
        align-items: center;
        padding: 12px 15px;
        margin-bottom: 12px;
        background: rgba(139, 69, 19, 0.08);
        border-radius: 6px;
        cursor: var(--cursor-hover);
        transition: all 0.2s ease;
        border-left: 3px solid transparent;
    }

    .item:hover {
        background: rgba(139, 69, 19, 0.15);
        border-left: 3px solid #8B4513;
        transform: translateX(5px);
    }

    .item:active {
        cursor: var(--cursor-active);
    }

    .item-icon {
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(139, 69, 19, 0.1);
        border-radius: 50%;
        margin-right: 15px;
        padding: 5px;
    }

    .item-icon img {
        width: 100%;
        height: auto;
        object-fit: contain;
    }

    .item-name {
        font-weight: bold;
        color: #5a3921;
        font-size: 16px;
    }

    .item-detail-title {
        font-size: 35px;
        color: #5a3921;
        margin-bottom: 20px;
        padding-bottom: 10px;
        border-bottom: 3px double #8B4513;
    }

    .item-description {
        font-style: italic;
        margin-bottom: 30px;
        line-height: 1.6;
        color: #5a3921;
        font-size: 15px;
        padding: 15px;
        background: rgba(139, 69, 19, 0.05);
        border-radius: 5px;
        border-left: 3px solid #8B4513;
    }

    .action-buttons {
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin-top: auto;
        padding-top: 20px;
    }

    .action-buttons .button {
        background: linear-gradient(to bottom, #a67c52, #8B4513);
        border: none;
        color: white;
        padding: 12px 20px;
        text-align: center;
        text-transform: uppercase;
        font-weight: bold;
        letter-spacing: 1px;
        cursor: var(--cursor-hover);
        border-radius: 5px;
        box-shadow: 0 4px 0 #5a3f25,
                    0 5px 10px rgba(0,0,0,0.1);
        transition: all 0.2s ease;
        width: 100%;
        max-width: 200px;
    }

    .action-buttons .button:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 0 #5a3f25,
                    0 8px 15px rgba(0,0,0,0.1);
        filter: brightness(1.05);
    }

    .action-buttons .button:active {
        transform: translateY(1px);
        box-shadow: 0 2px 0 #5a3f25;
        cursor: var(--cursor-active);
    }

    /* Enhanced paper texture */
    .page::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect width="100" height="100" fill="none" stroke="%238B4513" stroke-width="0.3" stroke-dasharray="3,3" opacity="0.2"/></svg>');
        pointer-events: none;
        z-index: 1;
    }

    /* Book spine effect */
    .book-pages::after {
        content: "";
        position: absolute;
        left: 50%;
        top: 0;
        bottom: 0;
        width: 15px;
        background: linear-gradient(to right, #5a3f25, #8B4513, #5a3f25);
        transform: translateX(-50%);
        z-index: 3;
        box-shadow: inset 0 0 10px rgba(0,0,0,0.5);
    }
`;

export class InventoryComponent extends HTMLElement {
    private readonly _gameRouteService: GameRouteService = new GameRouteService();
    private readonly _gameEventService: GameEventService = new GameEventService();
    private _selectedGameObjects: Set<GameObjectReference> = new Set<GameObjectReference>();
    private _selectedActionButton?: ActionReference;
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        this.render();
    }

    private render(): void {
        if (!this.shadowRoot) return;

        this.shadowRoot.innerHTML = `
            <style>${styles}</style>
            <div class="inventory-container">
                <button class="close-btn" title="Close Inventory">X</button>
                <div class="inventory-book">
                    <div class="book-cover"></div>
                    
                    <div class="book-pages">
                        <!-- Left page (items list) -->
                        <div class="page left-page">
                            <div class="page-content">
                                <h2 class="page-title">Inventory</h2>
                                
                                <div class="items-list">
                                    <div class="item" id="tutorial">
                                        <div class="item-icon">📖</div>
                                        <div class="item-name">Tutorial</div>
                                    </div>
                                    <!-- Items will be dynamically added here -->
                                </div>
                            </div>
                        </div>
                        
                        <!-- Right page (item details) -->
                        <div class="page right-page">
                            <div class="page-content">
                                <h2 class="item-detail-title">Tutorial</h2>
                                
                                <div class="item-description">
                                    BOOK OF INSTRUCTIONS.<br>
                                    KEEPS YOU WELL INFORMED.
                                </div>

                                <div class="action-buttons">
                                    <!-- Action buttons will appear here -->
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        `;

        this.shadowRoot.querySelector(".close-btn")?.addEventListener("click", () => this.loadCloseInventory());
        void this.selectItem();

        this.renderTutorialBook();
    }

    private async selectItem(): Promise<void> {
        const inventoryItems: Inventory[] = await this.getItemData();

        inventoryItems.forEach(item => {
            this.renderItem(item);
        });
    }

    private renderItem(item: Inventory): void {
        if (!this.shadowRoot) return;

        const itemsContainer: HTMLDivElement = this.shadowRoot.querySelector(".items-list") as HTMLDivElement;

        const itemDiv: HTMLDivElement = document.createElement("div");
        itemDiv.tabIndex = 0;
        itemDiv.className = "item";

        const itemIcon: HTMLDivElement = document.createElement("div");
        itemIcon.className = "item-icon";
        itemIcon.innerHTML = `
            <img src="/assets/img/rooms/layers/${item.image?.url}" />
        `;

        const itemName: HTMLDivElement = document.createElement("div");
        itemName.className = "item-name";
        itemName.textContent = item.name;

        itemDiv.appendChild(itemIcon);
        itemDiv.appendChild(itemName);

        itemDiv.addEventListener("mouseenter", () => this.playSound("button-hover.mp3"));
        itemDiv.addEventListener("click", async () => await this.renderItemDescription(item));

        itemsContainer.appendChild(itemDiv);
    }

    private async renderItemDescription(item: Inventory): Promise<void> {
        // Clear previous selections
        this._selectedGameObjects.clear();
        this._selectedActionButton = undefined;

        const actionButtonsDiv: HTMLDivElement = this.shadowRoot!.querySelector(".action-buttons") as HTMLDivElement;
        actionButtonsDiv.innerHTML = "";

        const selectedItem: GameObjectReference = {
            alias: item.alias,
            name: item.name,
        };
        this._selectedGameObjects.add(selectedItem);

        const rightPage: HTMLDivElement = this.shadowRoot!.querySelector(".right-page") as HTMLDivElement;
        (rightPage.querySelector(".item-detail-title") as HTMLHeadingElement).textContent = item.name;
        (rightPage.querySelector(".item-description") as HTMLDivElement).innerHTML = item.description;

        await this.getActionsForItem(item.alias);
    }

    private async getItemData(): Promise<Inventory[]> {
        const inventoryItems: Inventory[] = await this._gameRouteService.getInventory();
        return inventoryItems;
    }

    private async getActionsForItem(itemAlias: string): Promise<void> {
        const actionsForItem: ActionReference[] = await this._gameRouteService.getActionsForObject(itemAlias);
        const eatAndRead: ActionReference[] = actionsForItem.filter((action: ActionReference) =>
            action.alias === "read" || action.alias === "eat"
        );
        if (eatAndRead.length === 0) {
            // console.error("No actions for this item");
            return;
        }
        else {
            this.renderActionButtons(eatAndRead);
        }
    }

    private renderActionButtons(actionsForItem: ActionReference[]): void {
        const actionButtonsDiv: HTMLDivElement = this.shadowRoot!.querySelector(".action-buttons") as HTMLDivElement;
        actionButtonsDiv.append(this.getActionButtonDiv(actionsForItem));
    }

    private getActionButtonDiv(actionsForItem: ActionReference[]): HTMLElement {
        return html`
            <div class="action-buttons">
                ${actionsForItem.map(button => this.renderActionButton(button))}
            </div>
        `;
    }

    private renderActionButton(button: ActionReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button ${button.alias}-btn ${this._selectedActionButton === button ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("mouseenter", () => this.playSound("button-hover.mp3"));
        element.addEventListener("click", () => this.handleClickAction(button));

        return element;
    }

    private renderTutorialBook(): void {
        const tutorialItem: HTMLDivElement = this.shadowRoot?.querySelector("#tutorial") as HTMLDivElement;

        tutorialItem.addEventListener("click", () => {
            const rightPage: HTMLDivElement = this.shadowRoot!.querySelector(".right-page") as HTMLDivElement;

            (rightPage.querySelector(".item-detail-title") as HTMLHeadingElement).textContent = "Tutorial";
            (rightPage.querySelector(".item-description") as HTMLDivElement).innerHTML = "BOOK OF INSTRUCTIONS.<br>KEEPS YOU WELL INFORMED.";

            const actionButtonsDiv: HTMLDivElement = this.shadowRoot!.querySelector(".action-buttons") as HTMLDivElement;
            actionButtonsDiv.innerHTML = "";
        });
    }

    private playSound(name: string, type?: string): void {
        this._gameEventService.playSound(name, type);
    }

    private async handleClickAction(button: ActionReference): Promise<void> {
        let state: GameState | ReadingResult | undefined = undefined;
        // const item: GameObjectReference | undefined = [...this._selectedGameObjects][0];
        if (button.alias === "read") {
            state = await this._gameRouteService.getReadingPages(
                button.alias,
                [...this._selectedGameObjects].map(e => e.alias)
            );
        }
        // else if (button.alias === "eat") {
        //     await this._gameRouteService.eatItem(item.alias);
        // }
        else {
            state = await this._gameRouteService.executeAction(
                button.alias,
                [...this._selectedGameObjects].map(e => e.alias)
            );
        }

        this.playSound(button.alias, "action");

        if (state === undefined) {
            await this.loadCloseInventory();
        }
        else if ("images" in state) {
            const rootComponent: CanvasComponent | null = document.querySelector("game-root");

            if (rootComponent && rootComponent.shadowRoot) {
                const gameCanvas: HTMLElement | null = rootComponent.shadowRoot.querySelector("game-canvas");
                if (gameCanvas) {
                    (gameCanvas as CanvasComponent).sendImagesToReadComponent(state);

                    this._selectedActionButton = undefined;
                    await this._gameRouteService.closeInventory();
                    gameCanvas.shadowRoot!.querySelector(".game-over-overlay")?.remove();
                }
            }
        }
        else {
            await this.loadCloseInventory();
        }
    }

    private async loadCloseInventory(): Promise<void> {
        this._selectedActionButton = undefined;
        this.playSound("button-click.mp3");
        await this._gameRouteService.closeInventory();

        const canvasComponent: CanvasComponent | null = document.querySelector("game-root");

        if (canvasComponent && canvasComponent.shadowRoot) {
            const gameCanvas: HTMLElement | null = canvasComponent.shadowRoot.querySelector("game-canvas");
            if (gameCanvas) {
                void (gameCanvas as CanvasComponent).refreshGameState();
            }
        }
    }
}
